import React, { useEffect, useState } from "react";
import { Check, Star } from "lucide-react";
import ContactSection from "../components/ContactSection";
import { useToast } from "../hooks/use-toast";
import { fetchPlans, submitPlanInquiry } from "../lib/api";

export default function PlansPage() {
  const [billing, setBilling] = useState("Monthly");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    fetchPlans()
      .then((data) => {
        if (!cancelled) setPlans(data);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = async (p) => {
    setSubmittingId(p.id);
    try {
      await submitPlanInquiry({ plan_id: p.id, plan_name: p.name });
      toast({
        title: `${p.name} plan selected`,
        description: "Thanks! We\u2019ll reach out shortly to onboard you.",
      });
    } catch (e) {
      toast({
        title: "Could not record your selection",
        description:
          e?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="fade-in">
      <section className="bg-[var(--ka-cream)]">
        <div className="container-ka pt-16 pb-10 text-center">
          <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-3">
            Plans &amp; Pricing
          </span>
          <h1 className="font-serif text-5xl md:text-6xl text-[var(--ka-teal-darker)] mb-4">
            Pick a plan that fits your goal
          </h1>
          <p className="text-[var(--ka-muted)] max-w-2xl mx-auto">
            Transparent, no-fuss pricing. Pause, swap or cancel any time.
          </p>
          <div className="mt-8 inline-flex bg-white rounded-full p-1 border border-gray-200">
            {["Weekly", "Monthly"].map((b) => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                className={`px-5 py-2 text-sm rounded-full transition-colors ${
                  billing === b
                    ? "bg-[var(--ka-teal)] text-white"
                    : "text-[var(--ka-text)]"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--ka-cream)] pb-20">
        <div className="container-ka grid grid-cols-1 md:grid-cols-3 gap-6">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="plan-card p-7 rounded-md animate-pulse h-[440px]"
              >
                <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
                <div className="h-10 w-40 bg-gray-200 rounded mb-6" />
                <div className="space-y-3">
                  <div className="h-3 w-full bg-gray-200 rounded" />
                  <div className="h-3 w-full bg-gray-200 rounded" />
                  <div className="h-3 w-5/6 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          {!loading &&
            plans.map((p) => {
              const matches = billing === p.duration;
              const isSubmitting = submittingId === p.id;
              return (
                <div
                  key={p.id}
                  className={`plan-card p-7 rounded-md flex flex-col ${
                    p.badge ? "featured" : ""
                  } ${!matches ? "opacity-60" : ""}`}
                >
                  {p.badge && (
                    <span className="self-start text-[11px] tracking-[0.18em] uppercase bg-[var(--ka-teal)] text-white px-3 py-1 rounded-full mb-4 inline-flex items-center gap-1">
                      <Star size={12} /> {p.badge}
                    </span>
                  )}
                  <h3 className="font-serif text-3xl text-[var(--ka-teal-darker)] mb-1">
                    {p.name}
                  </h3>
                  <p className="text-sm text-[var(--ka-muted)] mb-5">
                    {p.duration} plan
                  </p>
                  <div className="mb-6">
                    <span className="font-serif text-5xl text-[var(--ka-teal-darker)]">
                      &#8377;{p.price.toLocaleString("en-IN")}
                    </span>
                    <span className="text-[var(--ka-muted)] text-sm ml-1">
                      /{p.duration === "Weekly" ? "week" : "month"}
                    </span>
                  </div>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className="flex gap-2 text-sm text-[var(--ka-text)]"
                      >
                        <Check
                          size={16}
                          className="mt-0.5 text-[var(--ka-teal)] shrink-0"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSelect(p)}
                    disabled={isSubmitting}
                    className={`${
                      p.badge ? "btn-teal" : "btn-outline-teal"
                    } disabled:opacity-60`}
                  >
                    {isSubmitting ? "Submitting..." : `Choose ${p.name}`}
                  </button>
                </div>
              );
            })}
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-ka grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            {
              t: "Free delivery",
              d: "Across Chennai, hot and on time, every meal.",
            },
            {
              t: "Pause anytime",
              d: "Travelling? Pause your plan with a quick WhatsApp message.",
            },
            {
              t: "Macro-tracked",
              d: "Every dish is precisely measured to keep you in ketosis.",
            },
          ].map((x) => (
            <div key={x.t}>
              <h3 className="font-serif text-2xl text-[var(--ka-teal-darker)] mb-2">
                {x.t}
              </h3>
              <p className="text-sm text-[var(--ka-muted)]">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
